import { ReactElement, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { getImageProps } from 'next/image';
import debounce from 'lodash.debounce';

import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';
import { useLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';
import { usePrefetchQuickAddProducts } from '@adapters/bigcommerce/hooks/product/usePrefetchQuickAddProducts';
import { FEATURE_FLAG_QUICK_ADD } from '@config/feature-flag';
import { isGiftCardHit } from '@utils/algolia/isGiftCardHit';
import Heading from '@components/ui/Heading';
import {
  trackHotspotTabClick,
  trackHotspotClick,
} from '_utils/GA4/trackHotspotEvents';
import ImageTabBar, { imageTabId } from './ImageTabBar';
import HotspotPopover from './HotspotPopover';
import HotspotDebugOverlay from './HotspotDebugOverlay';
import ReviewPanel from './ReviewPanel';
import { shouldPreferCompactQuote } from './quoteOverlap';
import type {
  ImageHotspotDot as ImageHotspotDotData,
  ImageHotspotTab,
} from '@adapters/prismic/transforms/imageHotspot';

import styles from './ImageHotspot.module.scss';

export interface ImageHotspotProps {
  tabs: ImageHotspotTab[];
  /** Optional heading rendered above the whole component (digi merch request, ED-345 comment) — distinct from each tab's own `heading` rendered by `ReviewPanel`. */
  heading?: string | null;
  /** Only the page's actual first/LCP-candidate slice should hint eager,
      high-priority loading — unlike `MainHero` (always the hero, always
      first), this slice can appear anywhere in a page's slice zone,
      including more than once, so hardcoding it would tell the browser to
      eagerly fetch every instance's image regardless of whether it's ever
      above the fold. Callers pass `sliceIndex === 0` (see
      `CategoryPageSliceHandler`/`LandingPageSliceHandler`). Defaults to
      `false`. Threaded into `getImageProps`' `fetchPriority` below, not
      `next/image`'s own `priority`/`preload` props — see the image markup's
      own comment for why. */
  priority?: boolean;
}

/**
 * "Shop the Gear / Shop the Look" banner (ED-345): tabbed lifestyle image with
 * per-tab hotspots. `ImageTabBar` (Figma 803:5624, its own dedicated "Image
 * Tab Bar" component — not the app's generic `Tabs`/`DropDown`) switches
 * between a desktop pill row and a mobile dropdown internally — only one tab
 * control renders when there's something to switch between (2+ tabs).
 *
 * The image itself is a native `<picture>` (see the render below) so the
 * browser — not React — picks the correct device's asset at first paint;
 * unlike a single `next/image` swapped via `useBreakpoint`, this can't
 * SSR-guess wrong and fetch both devices' images. The hotspot dots, however,
 * still can't be resolved until the client knows the real viewport
 * (`useBreakpoint` starts at width 0), and only one device's hotspot buttons
 * are ever mounted at a time — not both behind a CSS display toggle, which
 * would double the interactive elements per slice — so they're held back
 * behind `hasMounted` until that first client render settles, rather than
 * risking a flash of the wrong device's dots over the (now correctly
 * resolved) image.
 */
type Device = 'desktop' | 'mobile';

/** Pure (no hooks), so it's safe to call both ahead of the `tabs.length === 0`
    early return below (for `usePrefetchQuickAddProducts`, which must stay
    unconditional) and again after it for the actual render — one definition
    instead of two independently-maintained copies of "resolve the active
    tab". Falls back to the first tab exactly like the render path always has. */
const resolveActiveTab = (
  tabs: ImageHotspotTab[],
  activeTabId: string
): ImageHotspotTab | undefined =>
  tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];

/** Pure counterpart to `resolveActiveTab` — the device-specific hotspot list,
    defaulting to `[]` when there's no active tab yet. Sorted top-to-bottom,
    then left-to-right (`y` then `x`), rather than left in Prismic's
    authored slot order: the raw slot order has no relationship to where a
    hotspot actually sits on the image, so tabbing through it jumped around
    the photo unpredictably. Visual reading order gives keyboard/screen
    reader users a predictable path through the dots instead. */
const resolveHotspots = (
  tab: ImageHotspotTab | undefined,
  isMobileLayout: boolean
): ImageHotspotDotData[] =>
  ((isMobileLayout ? tab?.mobileHotspots : tab?.desktopHotspots) ?? [])
    .slice()
    .sort((a, b) => a.y - b.y || a.x - b.x);

const ImageHotspot = ({
  tabs,
  heading,
  priority = false,
}: ImageHotspotProps): ReactElement | null => {
  const { t } = useTranslation('common');
  const { asPath } = useRouter();
  const isMobileLayout = useBreakpoint(Breakpoints.md);
  // The desktop tabs' `aria-controls` target — completes the ARIA
  // tab/tabpanel relationship for `ImageTabBar`'s tablist (only one panel is
  // ever mounted at a time, its content simply swapping per active tab).
  const panelId = useId();
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id ?? '');
  // Tracked per device (not a shared index) since the desktop and mobile
  // hotspot lists for a tab are authored independently and don't line up
  // index-for-index — see SPEC.md's "desktop and mobile authored
  // independently" decision.
  const [openHotspot, setOpenHotspot] = useState<{
    device: Device;
    index: number;
  } | null>(null);
  // Set while the open hotspot's `QuickAdd` has an add in flight (its own
  // `view === 'spinner'`) — used below to keep that hotspot's tile pinned
  // open even if a different dot is clicked, rather than abandoning the
  // in-flight request.
  const [pendingHotspot, setPendingHotspot] = useState<{
    device: Device;
    index: number;
  } | null>(null);
  // `HotspotPopover`'s `collisionBoundary` — keeps every tile within the
  // photo rather than the whole viewport/page.
  const imageWrapperRef = useRef<HTMLDivElement | null>(null);
  // The tab bar's own rendered rect (only mounted with 2+ tabs) and the
  // desktop quote panel's — both fed into `shouldPreferCompactQuote` below.
  const navOverlayRef = useRef<HTMLDivElement | null>(null);
  const desktopQuotePanelRef = useRef<HTMLDivElement | null>(null);
  // Set when the (always-mounted, just CSS-hidden below `md`) desktop quote
  // panel would actually overlap a hotspot dot or the tab bar at the current
  // size — see `quoteOverlap.ts`'s own doc for why this is measured
  // geometry, not a fixed breakpoint tier.
  const [preferCompactQuote, setPreferCompactQuote] = useState(false);

  // `useBreakpoint` starts at width 0 (mobile), so `isMobileLayout` is only
  // trustworthy once the client has actually resolved the real viewport —
  // this flips true in a `useEffect` (which fires after `useBreakpoint`'s own
  // `useLayoutEffect` has already settled it, same commit), gating the
  // hotspot dots/debug overlay/prefetch below so they never render for a
  // device that turns out to be wrong a moment later. The image itself
  // doesn't need this — see its own comment in the render below.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  // Measures the desktop quote panel against the active hotspots/tab bar on
  // mount, tab change, and window resize (debounced, matching
  // `useBreakpoint`'s own 200ms) — window resizing is the one case where the
  // same tab's geometry can flip between overlapping and clear without any
  // of this effect's own dependencies changing, so it can't rely solely on
  // a dependency-driven re-run the way the other effects here do.
  // Skipped entirely below `md`: the mobile layout already shows the
  // compact treatment natively via CSS, and `desktopQuotePanelRef` measures
  // a `display: none` (zero-sized) element there regardless. A layout
  // effect (not a passive one), so the very first commit that actually
  // shows the desktop panel already carries the right decision into its
  // paint, instead of flashing the overlapping state for one frame first.
  useLayoutEffect(() => {
    if (isMobileLayout || !hasMounted) {
      setPreferCompactQuote(false);
      return undefined;
    }

    const recompute = () => {
      const imageEl = imageWrapperRef.current;
      const panelEl = desktopQuotePanelRef.current;
      if (!imageEl || !panelEl) {
        setPreferCompactQuote(false);
        return;
      }

      setPreferCompactQuote(
        shouldPreferCompactQuote({
          panelRect: panelEl.getBoundingClientRect(),
          containerRect: imageEl.getBoundingClientRect(),
          hotspots: resolveHotspots(
            resolveActiveTab(tabs, activeTabId),
            isMobileLayout
          ),
          navOverlayRect: navOverlayRef.current?.getBoundingClientRect(),
        })
      );
    };

    recompute();
    const debouncedRecompute = debounce(recompute, 200);
    window.addEventListener('resize', debouncedRecompute);
    return () => {
      debouncedRecompute.cancel();
      window.removeEventListener('resize', debouncedRecompute);
    };
  }, [isMobileLayout, hasMounted, activeTabId, tabs]);

  // Preloads every hotspot's Quick Add data as soon as the active tab/device
  // resolves — see `usePrefetchQuickAddProducts`'s own doc for why this is
  // safe to do eagerly here (a bounded ≤6 hotspots) but not on the PLP grid.
  // Resolved ahead of the `tabs.length === 0` early return below so this hook
  // call stays unconditional (rules of hooks) — `resolveActiveTab`/
  // `resolveHotspots` are plain functions, so calling them again after the
  // return (for the actual render) is free of that constraint. Gated the
  // same way `ProductTile` gates its own trigger — flag off or an ineligible
  // (OOS/in-store-only/gift-card) product means the trigger never renders,
  // so there's nothing worth warming the cache for — plus `hasMounted`,
  // since before then `isMobileLayout` may still be the wrong guess.
  usePrefetchQuickAddProducts(
    hasMounted && FEATURE_FLAG_QUICK_ADD
      ? resolveHotspots(resolveActiveTab(tabs, activeTabId), isMobileLayout)
          .filter(
            (hotspot) =>
              hotspot.product.availableOnPlp !== false &&
              hotspot.product.variants?.[hotspot.product.colour]
                ?.availableOnlinePurchase !== false &&
              !isGiftCardHit(hotspot.product.parentSku)
          )
          .map((hotspot) => hotspot.product.parentSku)
      : []
  );

  if (tabs.length === 0) {
    return null;
  }

  const activeTab = resolveActiveTab(tabs, activeTabId) as ImageHotspotTab;

  const handleTabChange = (tabId: string) => {
    // Switching tabs would abandon an in-flight add the same way switching
    // dots would (see `renderHotspots`'s `onOpenChange` below) — block it too.
    if (pendingHotspot) return;

    // ED-416 (1): reports the newly-selected tab, not a DOM element that was
    // "clicked" — `ImageTabBar`'s `onTabChange` fires the same way for a
    // mouse click, an arrow-key/Home/End move, and a mobile option select.
    const nextIndex = tabs.findIndex((tab) => tab.id === tabId);
    const nextTab = tabs[nextIndex];
    if (nextTab) {
      trackHotspotTabClick({
        data: {
          interactionLocation: asPath.split('?')[0],
          linkText: nextTab.label,
          coOpBrand: nextTab.coOpBrand,
          index: nextIndex,
        },
      });
    }

    setActiveTabId(tabId);
    setOpenHotspot(null);
  };

  const renderHotspots = (device: Device, hotspots: ImageHotspotDotData[]) =>
    hotspots.map((hotspot, index) => (
      <HotspotPopover
        // Keyed by tab + device, not just `index` alone: without them, a tab
        // switch or a breakpoint crossing (desktop <-> mobile hotspot lists
        // are independently authored, per the `openHotspot`/`pendingHotspot`
        // comments above) would reuse the *same* mounted instance for a
        // different product at that position — harmless before ED-416, but
        // `HotspotPopover`'s own `hotspot_view` tracking (`useObjectVisibility`,
        // fire-once) must run fresh per hotspot, not carry an already-fired
        // observer over to a product it was never actually shown for.
        // eslint-disable-next-line react/no-array-index-key -- hotspots have no stable id of their own; `index` is combined with `activeTab.id`/`device` above so it stays unique per hotspot across tab/device changes too, not just within one.
        key={`${activeTab.id}-${device}-${index}`}
        hotspot={hotspot}
        trackingContext={{
          interactionLocation: asPath.split('?')[0],
          tabName: activeTab.label,
          coOpBrand: activeTab.coOpBrand,
        }}
        index={index}
        open={openHotspot?.device === device && openHotspot.index === index}
        onOpenChange={(nextOpen, eventDetails) => {
          // An add is in flight for the currently open hotspot — freeze it
          // in place rather than abandoning the request. Only the open
          // hotspot's `QuickAdd` can ever be mounted (Base UI doesn't keep a
          // closed popover's content mounted), so `pendingHotspot` can only
          // ever refer to this same hotspot when it's set — no cross-hotspot
          // race to account for.
          if (pendingHotspot) {
            // Must actually cancel, not just skip the state update — Base UI
            // registers the trigger press into its own internal store
            // regardless, unless told otherwise, which otherwise left the
            // *other* dot stuck showing its "active" (`data-popup-open`)
            // styling even though it never opened (see the prop's own doc).
            eventDetails?.cancel();
            return;
          }
          // ED-416 (3): only the dot being opened, not every open/close
          // toggle — closing (re-pressing an open dot, switching tabs) isn't
          // a distinct engagement with this hotspot.
          if (nextOpen) {
            trackHotspotClick({
              data: {
                interactionLocation: asPath.split('?')[0],
                linkUrl: hotspot.product.url,
                linkText: hotspot.product.title,
                tabName: activeTab.label,
                coOpBrand: activeTab.coOpBrand,
                index,
              },
            });
          }
          setOpenHotspot(nextOpen ? { device, index } : null);
        }}
        onQuickAddPendingChange={(pending) =>
          // Functional update that bails out to the *same* object when the
          // value hasn't actually changed — a defensive belt-and-suspenders
          // alongside `QuickAdd`'s own ref-based fix (see its doc) against
          // this callback (necessarily a fresh closure every render, since
          // it closes over `device`/`index`) ever causing a render loop by
          // repeatedly setting state to a new-but-equal object.
          setPendingHotspot((prev) => {
            const next = pending ? { device, index } : null;
            const unchanged =
              (prev === null && next === null) ||
              (prev !== null &&
                next !== null &&
                prev.device === next.device &&
                prev.index === next.index);
            return unchanged ? prev : next;
          })
        }
        collisionBoundary={imageWrapperRef.current}
      />
    ));

  const device: Device = isMobileLayout ? 'mobile' : 'desktop';
  const activeHotspots = resolveHotspots(activeTab, isMobileLayout);

  // Native `<picture>` art direction (see the type doc above): both devices'
  // `getImageProps` are resolved every render — cheap, pure prop-generation,
  // no fetch of its own — and the browser (not this component) picks exactly
  // one `srcSet` to actually download, based on which `<source media>` below
  // matches. `getImageProps` itself has no side effect (unlike the live
  // `<Image>` component, it never injects a `<link rel="preload">`), so
  // passing `priority` through is safe here — there's only ever one `<img>`
  // element in the DOM regardless of which `<source>` wins, so eager-loading
  // it can't cause the double-fetch a `priority`'d `<Image>` would if two of
  // them were ever mounted at once.
  const {
    props: { srcSet: desktopSrcSet, sizes: desktopSizes },
  } = getImageProps({
    src: activeTab.desktopImage.url,
    alt: activeTab.desktopImage.alt ?? '',
    fill: true,
    sizes: '100vw',
    priority,
    fetchPriority: priority ? 'high' : undefined,
  });
  const { props: mobileImageProps } = getImageProps({
    src: activeTab.mobileImage.url,
    // A `<picture>` exposes exactly one `alt` regardless of which `<source>`
    // wins — the two devices' crops are the same photo, so desktop's alt
    // (falling back to mobile's if desktop left it blank) describes either.
    alt: activeTab.desktopImage.alt || activeTab.mobileImage.alt || '',
    fill: true,
    sizes: '100vw',
    priority,
    fetchPriority: priority ? 'high' : undefined,
    className: styles.image,
  });

  return (
    <section className={styles.imageHotspot}>
      {heading && (
        <div className={styles.headingContainer}>
          <Heading semanticLevel="h2" styleLevel="2" className={styles.heading}>
            {heading}
          </Heading>
        </div>
      )}
      <div className={styles.mediaContainer}>
        {/* Overlaid on the image (Figma) rather than a separate row above
            it — top-left on both devices. Nothing to switch between with a
            single tab, so no control renders at all; otherwise only the
            control for the current device mounts (matches the image/hotspot
            split below — no hidden-but-mounted dropdown/popover). Rendered
            before `.imageWrapper` (its own `z-index: 2` keeps it visually on
            top regardless of source order — see ImageHotspot.module.scss) so
            it's the first thing focus/tab order reaches, ahead of the
            hotspot dots — matching how it reads visually (top-left, above
            the dots scattered over the photo). */}
        {tabs.length > 1 && (
          <div className={styles.navOverlay} ref={navOverlayRef}>
            <ImageTabBar
              tabs={tabs.map((tab) => ({ id: tab.id, label: tab.label }))}
              activeTabId={activeTab.id}
              onTabChange={handleTabChange}
              isMobileLayout={isMobileLayout}
              panelId={panelId}
              ariaLabel={t('imageHotspot.categoryTabsAriaLabel')}
            />
          </div>
        )}

        {/* The single panel `ImageTabBar`'s desktop tabs control (ARIA
            tab/tabpanel relationship) — its content swaps per active tab
            rather than one panel existing per tab. Only given the
            role/id/label when there's actually a tablist controlling it. */}
        <div
          {...(tabs.length > 1
            ? {
                role: 'tabpanel',
                id: panelId,
                'aria-labelledby': imageTabId(panelId, activeTab.id),
              }
            : {})}
        >
          <div ref={imageWrapperRef} className={styles.imageWrapper}>
            <picture>
              <source
                media={`(min-width: ${Breakpoints.md}px)`}
                srcSet={desktopSrcSet}
                sizes={desktopSizes}
              />
              <img {...mobileImageProps} />
            </picture>
            {/* Bottom-anchored gradient scrim (Figma) so the heading/quote
                text stays legible over whatever the photo happens to be —
                painted above the image, below the hotspots/tile/text. */}
            <div className={styles.imageOverlay} aria-hidden="true" />
            {hasMounted && renderHotspots(device, activeHotspots)}
            {hasMounted && <HotspotDebugOverlay device={device} />}
          </div>

          <ReviewPanel
            // Remounts on tab change so mobileQuoteOpen resets instead of
            // carrying an expanded quote over to the newly active tab.
            key={activeTab.id}
            ref={desktopQuotePanelRef}
            heading={activeTab.heading}
            headingSemanticLevel={heading ? 'h3' : 'h2'}
            quote={activeTab.quote}
            quoteAttributionLine1={activeTab.quoteAttributionLine1}
            quoteAttributionLine2={activeTab.quoteAttributionLine2}
            quoteEyebrowLabel={activeTab.quoteEyebrowLabel}
            quoteTriggerLabel={activeTab.quoteTriggerLabel}
            dynamicPeek={activeTab.dynamicPeek}
            ctas={activeTab.ctas}
            tabName={activeTab.label}
            coOpBrand={activeTab.coOpBrand}
            preferCompactQuote={preferCompactQuote}
          />
        </div>
      </div>
    </section>
  );
};

export default ImageHotspot;
